import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo, useSyncExternalStore } from 'react';
import { io, Socket } from 'socket.io-client';
import { useUser } from '@clerk/clerk-react';
import { useOrganizationContext } from '@/contexts/OrganizationContext';
import { soundstageActiveStore } from '@/lib/soundstageActiveStore';
import type { MeetingParticipantProfile, LiveStatus } from '@/types/soundstage';

// Determine WebSocket URL (same server as chat, different namespace)
function getWebSocketUrl(): string {
    if (import.meta.env.VITE_CHAT_WS_URL) {
        return import.meta.env.VITE_CHAT_WS_URL;
    }

    const hostname = window.location.hostname;

    if (hostname === 'os.openinsurance.ai' || hostname === 'www.openinsurance.ai') {
        return 'https://organization-chat-production.up.railway.app';
    }

    if (hostname === 'qaos.openinsurance.ai') {
        return 'https://organization-chat-qa.up.railway.app';
    }

    return 'http://localhost:3001';
}

const WEBSOCKET_URL = getWebSocketUrl();

interface MeetingEvent {
    type: 'started' | 'ended' | 'participant:joined' | 'participant:left' | 'nudge';
    meetingId: string;
    user?: MeetingParticipantProfile;
    timestamp: string;
}

export interface PendingGuestKnock {
    guestId: string;
    guestName: string;
}

interface SoundstageWebSocketContextType {
    connected: boolean;

    // Meeting methods
    joinMeeting: (meetingId: string) => void;
    leaveMeeting: (meetingId: string) => void;
    startMeeting: (meetingId: string) => void;
    endMeeting: (meetingId: string) => void;
    nudge: (meetingId: string, targetUserProfileId: string) => void;
    updateLiveStatus: (meetingId: string, status: { camera: boolean; microphone: boolean; screenshare: boolean }) => void;
    notifyMeetingCreated: (meetingId: string) => void;
    notifyMeetingStateChanged: (meetingId: string) => void;
    approveGuest: (meetingId: string, guestId: string) => void;
    denyGuest: (meetingId: string, guestId: string) => void;

    // State
    participants: Record<string, MeetingParticipantProfile[]>;
    participantStatus: Record<string, LiveStatus[]>;
    activeMeetingId: string | null;
    meetingEvents: MeetingEvent[];
    pendingGuests: Record<string, PendingGuestKnock[]>;
    clearMeetingEvents: () => void;
}

const SoundstageWebSocketContext = createContext<SoundstageWebSocketContextType | null>(null);

export function SoundstageWebSocketProvider({ children }: { children: React.ReactNode }) {
    const { user } = useUser();
    const { currentOrganization } = useOrganizationContext();
    const socketRef = useRef<Socket | null>(null);
    const activeMeetingIdRef = useRef<string | null>(null);

    const isSoundstageAppRunning = useSyncExternalStore(
        soundstageActiveStore.subscribe,
        soundstageActiveStore.getSnapshot,
    );

    const [connected, setConnected] = useState(false);
    const [participants, setParticipants] = useState<Record<string, MeetingParticipantProfile[]>>({});
    const [participantStatus, setParticipantStatus] = useState<Record<string, LiveStatus[]>>({});
    const [activeMeetingId, setActiveMeetingId] = useState<string | null>(null);
    const [meetingEvents, setMeetingEvents] = useState<MeetingEvent[]>([]);
    const [pendingGuests, setPendingGuests] = useState<Record<string, PendingGuestKnock[]>>({});

    // Keep ref in sync with state for reconnect logic
    useEffect(() => {
        activeMeetingIdRef.current = activeMeetingId;
    }, [activeMeetingId]);

    // Reset state when app is closed
    useEffect(() => {
        if (!isSoundstageAppRunning) {
            setConnected(false);
            setParticipants({});
            setParticipantStatus({});
            setActiveMeetingId(null);
            setMeetingEvents([]);
            setPendingGuests({});
        }
    }, [isSoundstageAppRunning]);

    // WebSocket connection — only when soundstage app is running
    useEffect(() => {
        if (!user?.id || !isSoundstageAppRunning) return;

        const socket = io(`${WEBSOCKET_URL}/soundstage`, {
            transports: ['websocket', 'polling'],
            reconnection: true,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 30000,
            randomizationFactor: 0.5,
            reconnectionAttempts: Infinity,
        });

        socketRef.current = socket;

        socket.on('connect', () => {
            socket.emit('auth', {
                clerkUserId: user.id,
                organizationId: currentOrganization?.id,
            });
        });

        socket.on('authenticated', () => {
            setConnected(true);
            // Re-join active meeting on reconnect
            if (activeMeetingIdRef.current) {
                socket.emit('meeting:join', { meetingId: activeMeetingIdRef.current });
            }
        });

        socket.on('disconnect', () => {
            setConnected(false);
        });

        socket.on('meeting:joined', (data: { meetingId: string; participants: MeetingParticipantProfile[] }) => {
            setActiveMeetingId(data.meetingId);
            setParticipants(prev => ({
                ...prev,
                [data.meetingId]: data.participants,
            }));
        });

        socket.on('meeting:participant:joined', (data: { meetingId: string; user: MeetingParticipantProfile }) => {
            setParticipants(prev => {
                const existing = prev[data.meetingId] || [];
                if (existing.some(p => p.id === data.user.id)) return prev;
                return {
                    ...prev,
                    [data.meetingId]: [...existing, data.user],
                };
            });
            setMeetingEvents(prev => [...prev, {
                type: 'participant:joined',
                meetingId: data.meetingId,
                user: data.user,
                timestamp: new Date().toISOString(),
            }].slice(-100));
        });

        socket.on('meeting:participant:left', (data: { meetingId: string; user: MeetingParticipantProfile }) => {
            setParticipants(prev => ({
                ...prev,
                [data.meetingId]: (prev[data.meetingId] || []).filter(p => p.id !== data.user.id),
            }));
            setParticipantStatus(prev => ({
                ...prev,
                [data.meetingId]: (prev[data.meetingId] || []).filter(s => s.userProfileId !== data.user.id),
            }));
            setMeetingEvents(prev => [...prev, {
                type: 'participant:left',
                meetingId: data.meetingId,
                user: data.user,
                timestamp: new Date().toISOString(),
            }].slice(-100));
        });

        socket.on('meeting:started', (data: { meetingId: string; startedBy: MeetingParticipantProfile }) => {
            setMeetingEvents(prev => [...prev, {
                type: 'started',
                meetingId: data.meetingId,
                user: data.startedBy,
                timestamp: new Date().toISOString(),
            }].slice(-100));
        });

        socket.on('meeting:ended', (data: { meetingId: string; endedBy: MeetingParticipantProfile }) => {
            setActiveMeetingId(prev => prev === data.meetingId ? null : prev);
            // Clear participants and status for ended meeting
            setParticipants(prev => {
                const next = { ...prev };
                delete next[data.meetingId];
                return next;
            });
            setParticipantStatus(prev => {
                const next = { ...prev };
                delete next[data.meetingId];
                return next;
            });
            setMeetingEvents(prev => [...prev, {
                type: 'ended',
                meetingId: data.meetingId,
                user: data.endedBy,
                timestamp: new Date().toISOString(),
            }].slice(-100));
        });

        socket.on('meeting:nudge', (data: { meetingId: string; from: MeetingParticipantProfile }) => {
            setMeetingEvents(prev => [...prev, {
                type: 'nudge',
                meetingId: data.meetingId,
                user: data.from,
                timestamp: new Date().toISOString(),
            }].slice(-100));
        });

        // Org-wide: another user created a meeting — invalidate meeting list queries only
        socket.on('meeting:new', () => {
            (window as any).__queryClient?.invalidateQueries?.({ queryKey: ['soundstage', 'meetings'] });
        });

        // Org-wide: someone joined/left a meeting — refresh meeting lists only
        socket.on('meeting:participant_changed', () => {
            (window as any).__queryClient?.invalidateQueries?.({ queryKey: ['soundstage', 'meetings'] });
        });

        // Org-wide: meeting ended/cancelled/updated — refresh lists only
        socket.on('meeting:state_changed', () => {
            (window as any).__queryClient?.invalidateQueries?.({ queryKey: ['soundstage', 'meetings'] });
        });

        // Guest knock-to-enter — host receives these
        socket.on('meeting:guest_knocking', (data: { meetingId: string; guestId: string; guestName: string }) => {
            setPendingGuests(prev => {
                const list = prev[data.meetingId] || [];
                if (list.some(g => g.guestId === data.guestId)) return prev;
                return {
                    ...prev,
                    [data.meetingId]: [...list, { guestId: data.guestId, guestName: data.guestName }],
                };
            });
        });

        socket.on('meeting:pending_guests', (data: { meetingId: string; guests: PendingGuestKnock[] }) => {
            setPendingGuests(prev => ({ ...prev, [data.meetingId]: data.guests }));
        });

        socket.on('meeting:knock_cleared', (data: { meetingId: string; guestId: string }) => {
            setPendingGuests(prev => {
                const list = prev[data.meetingId];
                if (!list) return prev;
                const next = list.filter(g => g.guestId !== data.guestId);
                return { ...prev, [data.meetingId]: next };
            });
        });

        socket.on('meeting:live_status', (data: { meetingId: string } & LiveStatus) => {
            setParticipantStatus(prev => {
                const existing = prev[data.meetingId] || [];
                const filtered = existing.filter(s => s.userProfileId !== data.userProfileId);
                return {
                    ...prev,
                    [data.meetingId]: [...filtered, {
                        userProfileId: data.userProfileId,
                        camera: data.camera,
                        microphone: data.microphone,
                        screenshare: data.screenshare,
                    }],
                };
            });
        });

        return () => {
            socket.disconnect();
            socketRef.current = null;
        };
    }, [user?.id, currentOrganization?.id, isSoundstageAppRunning]);

    const joinMeeting = useCallback((meetingId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:join', { meetingId });
    }, [connected]);

    const leaveMeeting = useCallback((meetingId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:leave', { meetingId });
        setActiveMeetingId(prev => prev === meetingId ? null : prev);
        // Clear local participants/status for this meeting
        setParticipants(prev => {
            const next = { ...prev };
            delete next[meetingId];
            return next;
        });
        setParticipantStatus(prev => {
            const next = { ...prev };
            delete next[meetingId];
            return next;
        });
    }, [connected]);

    const startMeeting = useCallback((meetingId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:start', { meetingId });
    }, [connected]);

    const endMeeting = useCallback((meetingId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:end', { meetingId });
    }, [connected]);

    const nudge = useCallback((meetingId: string, targetUserProfileId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:nudge', { meetingId, targetUserProfileId });
    }, [connected]);

    const updateLiveStatus = useCallback((meetingId: string, status: { camera: boolean; microphone: boolean; screenshare: boolean }) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:live_status', { meetingId, ...status });
    }, [connected]);

    const notifyMeetingCreated = useCallback((meetingId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:notify_created', { meetingId });
    }, [connected]);

    const notifyMeetingStateChanged = useCallback((meetingId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:notify_created', { meetingId }); // reuse same broadcast
    }, [connected]);

    const clearMeetingEvents = useCallback(() => {
        setMeetingEvents([]);
    }, []);

    const approveGuest = useCallback((meetingId: string, guestId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:guest_approve', { meetingId, guestId });
    }, [connected]);

    const denyGuest = useCallback((meetingId: string, guestId: string) => {
        if (!socketRef.current || !connected) return;
        socketRef.current.emit('meeting:guest_deny', { meetingId, guestId });
    }, [connected]);

    const contextValue = useMemo(() => ({
        connected,
        joinMeeting,
        leaveMeeting,
        startMeeting,
        endMeeting,
        nudge,
        updateLiveStatus,
        notifyMeetingCreated,
        notifyMeetingStateChanged,
        approveGuest,
        denyGuest,
        participants,
        participantStatus,
        activeMeetingId,
        meetingEvents,
        pendingGuests,
        clearMeetingEvents,
    }), [
        connected,
        joinMeeting,
        leaveMeeting,
        startMeeting,
        endMeeting,
        nudge,
        updateLiveStatus,
        notifyMeetingCreated,
        notifyMeetingStateChanged,
        approveGuest,
        denyGuest,
        participants,
        participantStatus,
        activeMeetingId,
        meetingEvents,
        pendingGuests,
        clearMeetingEvents,
    ]);

    return (
        <SoundstageWebSocketContext.Provider value={contextValue}>
            {children}
        </SoundstageWebSocketContext.Provider>
    );
}

export function useSoundstageWebSocket() {
    const context = useContext(SoundstageWebSocketContext);
    if (!context) {
        throw new Error('useSoundstageWebSocket must be used within SoundstageWebSocketProvider');
    }
    return context;
}
